import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { AuthApiError, getAccount } from './api';
import { AuthenticatorCard } from './AuthenticatorCard';
import { PasskeysCard, type RunSensitive } from './PasskeysCard';
import { RecoveryCodesCard } from './RecoveryCodesCard';
import { RecoveryCodesView } from './RecoveryCodesView';
import { SessionsCard } from './SessionsCard';
import { StepUpPanel } from './StepUpPanel';
import { useSignOut } from './useSignOut';

const ACCOUNT_KEY = ['auth-account'];

export function AccountPage() {
  const client = useQueryClient();
  const signOut = useSignOut();
  const account = useQuery({ queryKey: ACCOUNT_KEY, queryFn: getAccount });
  const [pending, setPending] = useState<(() => Promise<unknown>) | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);

  const refresh = () => client.invalidateQueries({ queryKey: ACCOUNT_KEY });

  /**
   * Runs a sensitive change. If the server says "confirm it's you first", it waits behind
   * the confirm panel and runs again once that passes.
   */
  const run: RunSensitive = async (action) => {
    try {
      await action();
    } catch (err) {
      if (err instanceof AuthApiError && err.code === 'step_up_required') {
        setPending(() => action);
        return;
      }
      throw err;
    }
  };

  async function verified() {
    const action = pending;
    setPending(null);
    await refresh();
    if (action) await run(action);
  }

  return (
    <main className="page">
      <p className="back">
        <Link to="/">← Home</Link>
      </p>
      <h1>Account</h1>

      {account.isPending && (
        <section className="card" role="status" data-state="loading">
          <p className="card-title">Loading…</p>
        </section>
      )}
      {account.isError && (
        <section className="card card-bad" role="alert" data-state="error">
          <p className="card-title">Couldn't load your account</p>
          <button type="button" onClick={() => void account.refetch()}>
            Try again
          </button>
        </section>
      )}

      {account.data && (
        <div className="stack">
          {pending && (
            <StepUpPanel
              hasAuthenticator={account.data.authenticator.enabled}
              onVerified={verified}
              onCancel={() => setPending(null)}
            />
          )}
          {codes && (
            <section className="card card-warn">
              <RecoveryCodesView codes={codes} onDone={() => setCodes(null)} />
            </section>
          )}
          <PasskeysCard account={account.data} run={run} refresh={refresh} />
          <AuthenticatorCard
            account={account.data}
            run={run}
            refresh={refresh}
            onCodes={setCodes}
          />
          <RecoveryCodesCard
            account={account.data}
            run={run}
            refresh={refresh}
            onCodes={setCodes}
          />
          <SessionsCard />
          <p>
            <button type="button" onClick={() => void signOut(false)}>
              Sign out of this device
            </button>
          </p>
        </div>
      )}
    </main>
  );
}
